/* ============================================================
   DRAWLZ PDP — shared behavior. Vanilla JS, one IIFE, no framework.
   The ONLY sanctioned cross-section channel (Part 3):
     · shared size state (hero / second close / sticky bars)
     · shared quantity state (Round 15 — hero + second-close steppers)
     · shared Kaching bundle selection (Round 10 — variants-changed events)
     · shared rotation selection (Round 12 — the set-ladder upgrade box)
     · grouped review aggregate (hero blocks → reviews header, Round 11)
     · sticky-CTA visibility (observes inline CTA + second close)
     · horizontal-scroll fade affordances (Round 17)
     · Difference answer expand + recommendations quick-add (18B/18E)
     · rating line → reviews scroll
   Every behavior degrades silently when its counterpart section is
   absent — nothing throws, nothing leaves a dead link.
   All DOM queries are scoped to a section root or data attribute.
   Add to cart posts to routes.cart_add_url and renders through the
   theme's own <cart-drawer> element — never a second cart system.
   ============================================================ */
(function () {
  'use strict';
  if (window.__drawlzPdpBooted) return; // every section loads this file; boot once
  window.__drawlzPdpBooted = true;
  var d = document;
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var scrollBehavior = reduced ? 'auto' : 'smooth';

  /* ---------- shared size state (Rule 10 — one source of truth) ---------- */
  var state = { size: null, color: null, variantId: null, available: true };
  var adding = false; // one ATC request at a time, across every form

  /* ---------- native Color × Size variant map (Round 20) ----------
     Emitted by the size selector's pills branch ONLY for multi-option
     products (SuedeSkin Crew, future tees) — stays null everywhere else
     and every vmap codepath no-ops. With a map present the ATC variant
     resolves from Color AND Size together; size pills carry no variant
     id on these products. */
  var vmap = null;
  function vmapAny(color, size) {
    if (!vmap) return false;
    for (var i = 0; i < vmap.variants.length; i++) {
      var v = vmap.variants[i];
      if (!v.available) continue;
      if (color != null && v.opts[vmap.colorIndex] !== color) continue;
      if (size != null && v.opts[vmap.sizeIndex] !== size) continue;
      return true;
    }
    return false;
  }
  function vmapResolve() {
    if (!state.size) return null;
    // a product WITH a color option requires a color pick (boot preselects
    // one); colorIndex -1 = no color option — resolve by size alone
    if (vmap.colorIndex >= 0 && !state.color) return null;
    for (var i = 0; i < vmap.variants.length; i++) {
      var v = vmap.variants[i];
      if (vmap.colorIndex >= 0 && v.opts[vmap.colorIndex] !== state.color) continue;
      if (v.opts[vmap.sizeIndex] !== state.size) continue;
      if (v.available) return v.id;
    }
    return null;
  }

  /* ---------- shared rotation selection (Round 12) ---------- */
  var rot = { tier: 0, variantId: null, price: null, title: null };
  function rotActive() { return rot.tier > 0 && !!rot.variantId; }

  /* ---------- hero swap on rotation select ----------
     Selecting a pack tier overlays that pack's composite (all its colorways) on the hero
     with a caption, so the shopper sees what is in the pack at the moment of the upsell.
     One Pair reverts.

     An OVERLAY, not a mutation. The base gallery DOM is never touched, so reverting to One
     Pair is exact for free and zoom / thumbnails / counter / size logic keep working. The
     alternative - rewriting slide <img> src - would have meant owning revert fidelity by
     hand and fighting the gallery JS that manages slides, thumbs and the counter.

     The figures are rendered late in the section (rot1/rot2 only resolve ~300 lines after
     the gallery markup), so they are MOVED into the stage here: appendChild, never
     cloneNode, so the DOM never holds two copies. Inert by construction where there is no
     rotation - nothing is rendered, hsFigs stays empty, every call returns immediately. */
  var hsFigs = null;                       /* { "1": <figure>, "2": <figure> } */
  function heroSwapInit() {
    if (hsFigs) return;
    hsFigs = {};
    var src = d.querySelector('[data-drawlz-heroswap-src]');
    var stage = d.querySelector('.drawlz-pdp-hero__stage');
    if (!src || !stage) return;             /* no rotation on this PDP, or no gallery */
    Array.prototype.slice.call(src.querySelectorAll('[data-drawlz-heroswap]')).forEach(function (fig) {
      hsFigs[fig.getAttribute('data-drawlz-heroswap')] = fig;
      stage.appendChild(fig);               /* MOVE, so nothing is duplicated */
    });
    if (src.parentNode) src.parentNode.removeChild(src);
  }
  function heroSwapApply(tier) {
    heroSwapInit();
    if (!hsFigs) return;
    Object.keys(hsFigs).forEach(function (k) {
      var on = String(tier) === k;
      hsFigs[k].hidden = !on;
      hsFigs[k].setAttribute('aria-hidden', on ? 'false' : 'true');
    });
  }

  /* ---------- shared Kaching bundle selection (Round 10) ---------- */
  var bundle = { items: null, price: null, tierQty: 0 };

  /* ---------- shared quantity (Round 15) ---------- */
  var qtyState = 1;
  function setQty(n) {
    n = parseInt(n, 10);
    if (!n || n < 1) n = 1;
    qtyState = n;
    d.querySelectorAll('[data-drawlz-qty] input[name="quantity"], input[data-drawlz-qty-native]').forEach(function (el) {
      el.value = String(qtyState);
    });
    emit();
  }
  function multiplyPrice(text, n) {
    var m = /\$([\d,]+(?:\.\d+)?)/.exec(text || '');
    if (!m) return null;
    var total = parseFloat(m[1].replace(/,/g, '')) * n;
    return '$' + total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function bundleTotal() {
    var n = 0, i;
    if (bundle.items) for (i = 0; i < bundle.items.length; i++) n += bundle.items[i].quantity || 0;
    return n;
  }
  function bundleMulti() {
    return !!bundle.items && (bundle.items.length > 1 || bundleTotal() > 1 || bundle.tierQty > 1);
  }
  var subs = [];
  function emit() { subs.forEach(function (fn) { fn(state); }); }
  /* ---------- sitewide size sync gate (v3) ----------
     True only when the hero says THIS page's [data-drawlz-size] row is the
     UNDERWEAR axis. The gate itself is computed in Liquid (three conditions -
     see the comment above the section root in sections/drawlz-pdp-hero.liquid);
     this only reads the answer.

     Queried fresh on every call rather than cached at boot. The theme editor
     re-renders a section without re-booting this file (__drawlzPdpBooted), so a
     cached flag would go stale the moment the owner switches template or product
     in the editor - and it would go stale SILENTLY, in the direction of writing a
     tee size into the sitewide underwear size. querySelector on one attribute,
     a handful of times per page, is not worth optimising. */
  function isUnderwearPdp() {
    var el = d.querySelector('.drawlz-pdp-hero[data-drawlz-underwear]');
    return !!el && el.getAttribute('data-drawlz-underwear') === '1';
  }

  function setSize(size, variantId) {
    state.size = size; state.variantId = variantId;
    if (vmap) state.variantId = vmapResolve();
    clearSizeMsg();
    emit();
    /* PDP -> sitewide. Guarded three ways, and every guard is load-bearing:
         · isUnderwearPdp()   - this page's pill row really is the underwear axis
         · isUnderwearScale   - the value is on the S-4XL scale. Catches the
                                "Like Father Like Son Pack!" Kidz axis and any
                                composite "L / 2XL" proxy value that slips through
         · get() !== size     - REQUIRED, not defensive. DrawlzSize.set() toggles:
                                re-passing the current value CLEARS it, so an
                                unguarded call would wipe the size when a shopper
                                re-picks the size they already had selected. */
    if (size && isUnderwearPdp() && window.DrawlzSize &&
        typeof window.DrawlzSize.get === 'function' &&
        typeof window.DrawlzSize.set === 'function' &&
        typeof window.DrawlzSize.isUnderwearScale === 'function' &&
        window.DrawlzSize.isUnderwearScale([size]) &&
        window.DrawlzSize.get() !== size) {
      window.DrawlzSize.set(size);
    }
  }
  function setColor(color) {
    state.color = color;
    if (vmap) state.variantId = vmapResolve();
    emit();
  }
  function onState(fn) { subs.push(fn); fn(state); }

  function clearSizeMsg() {
    d.querySelectorAll('[data-drawlz-size-msg]').forEach(function (el) { el.hidden = true; el.textContent = ''; });
  }
  function showSizeMsg(text) {
    d.querySelectorAll('[data-drawlz-size-msg]').forEach(function (el) { el.hidden = false; el.textContent = text; });
  }

  /* ---------- size selectors (one snippet, four render sites) ---------- */
  function initSizeSelectors() {
    d.addEventListener('click', function (e) {
      var pill = e.target.closest('[data-drawlz-size]');
      if (!pill || pill.disabled || pill.classList.contains('is-sold-out') || adding) return;
      setSize(pill.getAttribute('data-size'), pill.getAttribute('data-variant-id'));
    });
    d.addEventListener('change', function (e) {
      var sel = e.target.closest('[data-drawlz-size-select]');
      if (!sel || !sel.value || adding) return;
      var opt = sel.options[sel.selectedIndex];
      setSize(sel.value, opt.getAttribute('data-variant-id'));
    });
    onState(function (s) {
      d.querySelectorAll('[data-drawlz-size]').forEach(function (pill) {
        pill.setAttribute('aria-pressed', String(pill.getAttribute('data-size') === s.size));
      });
      d.querySelectorAll('[data-drawlz-size-select]').forEach(function (sel) {
        if (sel.value !== (s.size || '')) sel.value = s.size || '';
      });
    });
  }

  /* ---------- add to cart: fetch + theme cart drawer (Defect 1) ---------- */
  /* ---------- GA4: attribute set adds that came from the PDP rotation ----------

     WHY A CUSTOM EVENT AND NOT item_list_name ON add_to_cart.

     Nothing in this theme emits GA4 ecommerce. The chain is:

         Analyzify app extension (an_gtm.js)  ->  dataLayer  ee_* events
         GTM  GTM-KTDGPZG  (server container ss.drawlz.com)
         GA4  G-7CHXJBWMGX

     Analyzify owns add_to_cart, so the theme cannot add a list name to it. Hence a
     separate event the container can map on its own terms.

     AND A DEFECT WORTH KNOWING ABOUT. Analyzify builds add_to_cart from the PRODUCT
     THE PAGE IS ABOUT, not from what actually entered the cart. On an ordinary PDP
     those are the same thing. On this rotation they are not: measured 2026-08-13 on
     /products/black-mens-underwear, choosing "Core Five Originalz" put the right line
     in the cart (variant 49442091401431, $120) while GA4 was told "Originalz Black,
     $27" - the page's own product, understated by $93. Adding the same set from
     /products/core-five-originalz reports it correctly, which is what pins the cause
     on the page-context read rather than on the cart.

     So this event carries the TRUTH and the list name. It reads the /cart/add
     RESPONSE - the line Shopify actually created - never local UI state, so a zodiac
     variant swap or any other late rewrite is reported as what it became.

     It does NOT emit a second add_to_cart. Two add_to_cart events for one click would
     double-count. Mapping this to a GA4 event in GTM is the owner's call.

     One trap: the /cart/add response `quantity` is the RESULTING line quantity, not
     the amount just added - add the same set twice and it reads 2. The quantity we
     asked for is passed in instead, or a second add would report 2 units. */
  var UPSELL_LIST = 'pdp_set_upsell';

  function upsellCurrency() {
    try {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        return window.Shopify.currency.active;
      }
    } catch (e) {}
    return 'USD';
  }

  function upsellSourceProductId() {
    try {
      var m = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (m && m.product && m.product.id) return String(m.product.id);
      if (m && m.page && m.page.resourceId) return String(m.page.resourceId);
    } catch (e) {}
    return '';
  }

  function upsellSourceHandle() {
    try {
      var m = String(window.location.pathname).match(/\/products\/([^\/?#]+)/);
      return m ? m[1] : '';
    } catch (e) {}
    return '';
  }

  /* The response is normally the single created line; a multi-line add returns
     { items: [...] }. Accept either so a two-line bump is not silently dropped. */
  function upsellLines(json) {
    if (!json) return [];
    if (Array.isArray(json.items)) return json.items;
    if (json.id || json.variant_id) return [json];
    return [];
  }

  function pushUpsellAdd(json, snap) {
    try {
      var lines = upsellLines(json);
      if (!lines.length) return;

      var qty = snap && snap.qty > 0 ? snap.qty : 1;
      var items = [];
      var value = 0;

      for (var i = 0; i < lines.length; i++) {
        var L = lines[i];
        var price = typeof L.price === 'number' ? L.price / 100 : null;
        if (price !== null) value += price * qty;
        items.push({
          item_id: String(L.product_id || ''),
          item_variant_id: String(L.variant_id || L.id || ''),
          item_name: L.product_title || L.title || '',
          item_variant: L.variant_title || '',
          item_brand: L.vendor || '',
          item_category: L.product_type || '',
          item_sku: L.sku || '',
          item_handle: L.handle || '',
          price: price,
          quantity: qty,
          item_list_id: UPSELL_LIST,
          item_list_name: UPSELL_LIST
        });
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'pdp_upsell_add',
        ecommerce: {
          currency: upsellCurrency(),
          value: value,
          item_list_id: UPSELL_LIST,
          item_list_name: UPSELL_LIST,
          items: items
        },
        upsell: {
          source: 'pdp_rotation',
          source_product_id: upsellSourceProductId(),
          source_product_handle: upsellSourceHandle(),
          tier: snap && snap.tier ? snap.tier : null,
          tier_name: snap && snap.tierName ? snap.tierName : ''
        }
      });
    } catch (e) {
      /* Analytics must never break add-to-cart. Swallow and carry on. */
    }
  }

  function cartAddUrl() {
    return (window.routes && window.routes.cart_add_url) || '/cart/add';
  }
  function setAdding(on) {
    adding = on;
    d.body.classList.toggle('drawlz-atc-lock', on);
  }
  function showAtcError(form, text) {
    var box = form.querySelector('[data-drawlz-atc-error]');
    if (!box) return;
    var span = box.querySelector('[data-drawlz-atc-error-text]');
    if (span && text) span.textContent = text;
    box.hidden = false;
  }
  function hideAtcError(form) {
    var box = form.querySelector('[data-drawlz-atc-error]');
    if (box) box.hidden = true;
  }
  function submitForm(form) {
    if (adding) return;
    var cta = form.querySelector('[data-drawlz-atc]');
    if (!cta || cta.classList.contains('is-notify')) return;
    if (!state.variantId) return;
    setAdding(true);
    hideAtcError(form);
    cta.classList.add('is-adding');
    var labelAdding = cta.getAttribute('data-label-adding');
    if (labelAdding) cta.textContent = labelAdding;

    /* Snapshot the upsell context BEFORE the request. rot is shared mutable state
       and the response lands a few hundred ms later - reading rot.tier inside
       .then() would report whatever was clicked in the meantime. qty is fixed at 1
       here because that is what the rotation branch below posts. */
    var upsell = rotActive() ? { tier: rot.tier, tierName: rot.title, qty: 1 } : null;

    var req;
    if (rotActive()) {
      var rb = new FormData();
      rb.set('id', String(rot.variantId));
      rb.set('quantity', '1');
      rb.append('sections', 'cart-drawer,cart-icon-bubble');
      rb.append('sections_url', window.location.pathname);
      req = fetch(cartAddUrl(), {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: rb
      });
    } else if (bundle.items && bundle.items.length > 1) {
      var items = bundle.items.map(function (it) {
        return { id: it.id, quantity: it.quantity };
      });
      req = fetch(cartAddUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items, sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname })
      });
    } else {
      var qty = 1;
      if (bundle.items && bundle.items.length === 1) qty = bundle.items[0].quantity || 1;
      if (bundle.tierQty > qty) qty = bundle.tierQty;
      if (qty === 1 && qtyState > 1) qty = qtyState;
      var body = new FormData(form);
      body.set('id', state.variantId);
      body.set('quantity', String(qty));
      body.append('sections', 'cart-drawer,cart-icon-bubble');
      body.append('sections_url', window.location.pathname);
      req = fetch(cartAddUrl(), {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: body
      });
    }
    req
      .then(function (res) { return res.json().then(function (json) { return { ok: res.ok, json: json }; }); })
      .then(function (r) {
        if (!r.ok || r.json.status) {
          showAtcError(form, r.json && r.json.description ? String(r.json.description) : null);
          return;
        }

        /* The line exists in the cart now. If it came from the rotation, mark it for
           GA4 - see the note above pushUpsellAdd(). Reads r.json, the line Shopify
           actually created, not what the UI intended. */
        if (upsell) pushUpsellAdd(r.json, upsell);
        var drawer = d.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.classList.remove('is-empty');
          drawer.renderContents(r.json);
        } else {
          window.location.assign((window.routes && window.routes.cart_url) || '/cart');
        }
      })
      .catch(function () {
        showAtcError(form, null);
      })
      .finally(function () {
        setAdding(false);
        cta.classList.remove('is-adding');
        emit();
      });
  }

  /* ---------- CTA buttons + product forms + dynamic checkout guard ---------- */
  function initForms() {
    var forms = d.querySelectorAll('[data-drawlz-product-form]');
    onState(function (s) {
      forms.forEach(function (form) {
        var idInput = form.querySelector('input[name="id"]');
        var cta = form.querySelector('[data-drawlz-atc]');
        var dc = form.querySelector('[data-drawlz-dynamic-checkout]');
        if (idInput) idInput.value = s.variantId || '';
        if (cta && !cta.classList.contains('is-notify') && !cta.classList.contains('is-adding')) {
          var active = !!s.variantId;
          cta.classList.toggle('is-active', active);
          cta.setAttribute('aria-disabled', String(!active));
          var label = active ? cta.getAttribute('data-label-active') : cta.getAttribute('data-label-inactive');
          var priceOverride = (rotActive() && rot.price) ? rot.price
            : (bundleMulti() && bundle.price) ? bundle.price : null;
          if (!priceOverride && qtyState > 1) priceOverride = multiplyPrice(label, qtyState);
          if (active && priceOverride && /\$[\d,]+(\.\d+)?/.test(label)) {
            label = label.replace(/\$[\d,]+(\.\d+)?/, function () { return priceOverride; });
          }
          cta.textContent = label;
        }
        if (dc) {
          var pb = dc.querySelector('.shopify-payment-button');
          var dcEmpty = !pb || pb.childElementCount === 0;
          dc.hidden = !s.variantId || bundleMulti() || rotActive() || dcEmpty;
        }
      });
      d.querySelectorAll('.drawlz-pdp-sticky__price').forEach(function (el) {
        if (!el.getAttribute('data-drawlz-base-price')) el.setAttribute('data-drawlz-base-price', el.textContent);
        var base = el.getAttribute('data-drawlz-base-price');
        el.textContent = (rotActive() && rot.price) ? rot.price
          : (bundleMulti() && bundle.price) ? bundle.price
          : (qtyState > 1 && multiplyPrice(base, qtyState)) || base;
      });
      d.querySelectorAll('.drawlz-pdp-sticky__label').forEach(function (el) {
        var node = el.firstChild;
        if (!node || node.nodeType !== 3) return;
        if (!el.getAttribute('data-drawlz-base-label')) el.setAttribute('data-drawlz-base-label', node.textContent);
        node.textContent = (rotActive() && rot.title) ? rot.title : el.getAttribute('data-drawlz-base-label');
      });
      d.querySelectorAll('[data-drawlz-sticky-variant]').forEach(function (el) {
        var vtxt = s.size ? 'Size ' + s.size : '';
        if (vtxt && s.color) vtxt = s.color + ' · ' + vtxt;
        el.textContent = vtxt;
      });
      d.querySelectorAll('.drawlz-pdp-second-close__id').forEach(function (el) {
        if (!el.getAttribute('data-drawlz-base-id')) el.setAttribute('data-drawlz-base-id', el.textContent);
        el.textContent = (rotActive() && rot.title && rot.price)
          ? rot.title + ' — ' + rot.price
          : el.getAttribute('data-drawlz-base-id');
      });
    });
    if ('MutationObserver' in window) {
      d.querySelectorAll('[data-drawlz-dynamic-checkout] .shopify-payment-button').forEach(function (pb) {
        new MutationObserver(function () { emit(); }).observe(pb, { childList: true });
      });
    }
    d.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-drawlz-product-form]');
      if (!form) return;
      e.preventDefault();
      if (!state.variantId || adding) return;
      submitForm(form);
    });
    d.addEventListener('click', function (e) {
      var retry = e.target.closest('[data-drawlz-atc-retry]');
      if (!retry) return;
      var form = retry.closest('[data-drawlz-product-form]');
      if (form) submitForm(form);
    });
    d.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-drawlz-qty-step]');
      if (!btn || adding) return;
      setQty(qtyState + parseInt(btn.getAttribute('data-drawlz-qty-step'), 10));
    });
    d.addEventListener('change', function (e) {
      var input = e.target.closest('[data-drawlz-qty] input[name="quantity"]');
      if (input) setQty(input.value);
    });
    d.addEventListener('click', function (e) {
      var cta = e.target.closest('[data-drawlz-atc]');
      if (!cta) return;
      if (adding) { e.preventDefault(); return; }
      if (cta.getAttribute('aria-disabled') === 'true') {
        e.preventDefault();
        var form = cta.closest('[data-drawlz-product-form]');
        var scopeRow = form && form.closest('[data-drawlz-buy]') ? form.closest('[data-drawlz-buy]').querySelector('.drawlz-size__row') : d.querySelector('.drawlz-size__row');
        if (scopeRow) {
          var y = scopeRow.getBoundingClientRect().top + window.scrollY - 130;
          window.scrollTo({ top: y, behavior: scrollBehavior });
          scopeRow.classList.add('is-flash');
          setTimeout(function () { scopeRow.classList.remove('is-flash'); }, 1600);
        }
      }
    });
  }

  /* ---------- Kaching Bundles bridge (Round 10) ---------- */
  function initKaching() {
    var bound = [];
    var barQty = null;

    function loadBarQuantities(block) {
      if (barQty || !window.kachingBundlesApi || typeof window.kachingBundlesApi.fetchDeals !== 'function') return;
      barQty = {};
      var holder = block.closest('kaching-bundle');
      var pid = holder ? Number(holder.getAttribute('product-id')) : NaN;
      if (!pid) return;
      try {
        window.kachingBundlesApi.fetchDeals({ productId: pid }).then(function (deals) {
          (deals || []).forEach(function (deal) {
            (deal.dealBars || []).forEach(function (bar) {
              if (bar && bar.id != null && bar.quantity) barQty[bar.id] = bar.quantity;
            });
          });
        }).catch(function () { /* map stays empty */ });
      } catch (err) { /* same */ }
    }

    function bind(block) {
      if (bound.indexOf(block) !== -1) return;
      bound.push(block);
      loadBarQuantities(block);
      block.addEventListener('variants-changed', function (e) {
        var det = e.detail || {};
        var list = det.variantIdQuantities;
        if (!list || !list.length) {
          bundle.items = null; bundle.price = null;
        } else {
          bundle.items = list.map(function (v) { return { id: v.variantId, quantity: v.quantity || 1 }; });
          bundle.price = det.formattedPrice ||
            (det.pricing && det.pricing.discountedPrice && det.pricing.discountedPrice.formatted) || null;
        }
        emit();
      });
      block.addEventListener('deal-bar-selected', function (e) {
        var id = e.detail && e.detail.dealBarId;
        bundle.tierQty = (barQty && id != null && barQty[id]) || 0;
        emit();
      });
      block.addEventListener('variant-selected', function () { emit(); });
    }

    d.querySelectorAll('kaching-bundles-block').forEach(bind);
    if ('MutationObserver' in window) {
      new MutationObserver(function (muts) {
        muts.forEach(function (m) {
          Array.prototype.forEach.call(m.addedNodes, function (n) {
            if (n.nodeType !== 1) return;
            if (n.tagName.toLowerCase() === 'kaching-bundles-block') bind(n);
            else if (n.querySelectorAll) n.querySelectorAll('kaching-bundles-block').forEach(bind);
          });
        });
      }).observe(d.body, { childList: true, subtree: true });
    }
  }

  /* ---------- rotation mirror (Round 12 follow-up) ---------- */
  function initRotationMirror() {
    var mount = d.querySelector('[data-drawlz-rotation-mirror]');
    if (!mount) return;
    var src = d.querySelector('.drawlz-pdp-hero [data-drawlz-rotation]');
    if (!src) return;
    mount.appendChild(src.cloneNode(true));
  }

  /* ---------- rotation upgrade box (Round 12) ---------- */
  function initRotation() {
    var boxes = Array.prototype.slice.call(d.querySelectorAll('[data-drawlz-rotation]'));
    if (!boxes.length) return;
    var dataEl = boxes[0].querySelector('[data-drawlz-rotation-data]');
    var tiers = {};
    try {
      (JSON.parse(dataEl.textContent).tiers || []).forEach(function (t) {
        var bySize = {};
        (t.variants || []).forEach(function (v) { bySize[v.size] = v; });
        tiers[t.tier] = { name: t.name || t.title, priceFormatted: t.priceFormatted, bySize: bySize };
      });
    } catch (err) { return; }
    if (!tiers[1] && !tiers[2]) return;
    var both = !!(tiers[1] && tiers[2]);
    var tpl = {
      gate: boxes[0].getAttribute('data-msg-gate'),
      soldout: boxes[0].getAttribute('data-msg-soldout'),
      allSoldout: boxes[0].getAttribute('data-msg-all-soldout'),
      revert: boxes[0].getAttribute('data-msg-revert'),
      revertTier: boxes[0].getAttribute('data-msg-revert-tier'),
      /* cards only (owner, 18M follow-up): short per-card note above the
         struck title — "Sold out in {size}". Absent on classic markup. */
      cardSoldout: boxes[0].getAttribute('data-msg-card-soldout')
    };
    var views = boxes.map(function (box) {
      return {
        box: box,
        check1: box.querySelector('[data-drawlz-rotation-check="1"]'),
        check2: box.querySelector('[data-drawlz-rotation-check="2"]'),
        /* Round 18L: choice-card presentation (empty array on the classic
           checkbox markup — every card codepath below then no-ops) */
        cards: Array.prototype.slice.call(box.querySelectorAll('[data-drawlz-rotation-card]')),
        msg1: box.querySelector('[data-drawlz-rotation-msg="1"]'),
        msg2: box.querySelector('[data-drawlz-rotation-msg="2"]'),
        gate: box.querySelector('[data-drawlz-rotation-gate]'),
        notice: box.querySelector('[data-drawlz-rotation-notice]')
      };
    });
    function fill(t, name, size, fallback) {
      return (t || '').replace('{name}', name || '').replace('{size}', size || '').replace('{fallback}', fallback || '');
    }
    function setMsg(el, text) { if (el) { el.hidden = !text; el.textContent = text || ''; } }
    function avail(n, size) {
      var t = tiers[n];
      var v = t && size ? t.bySize[size] : null;
      return !!(v && v.available);
    }
    function setRow(view, n, enabled, struck) {
      var row = view.box.querySelector('[data-drawlz-rotation-tier="' + n + '"]');
      var check = n === 1 ? view.check1 : view.check2;
      if (check) check.disabled = !enabled;
      if (row) {
        row.classList.toggle('is-disabled', !enabled);
        row.classList.toggle('is-struck', !!struck);
      }
    }
    var lastAppliedTier = 0;
    function applySelection() {
      var t = rot.tier > 0 ? tiers[rot.tier] : null;
      var v = t && state.size ? t.bySize[state.size] : null;
      rot.variantId = v && v.available ? v.id : null;
      if (rot.tier > 0 && !rot.variantId) rot.tier = 0;
      rot.price = rot.tier > 0 ? tiers[rot.tier].priceFormatted : null;
      rot.title = rot.tier > 0 ? tiers[rot.tier].name : null;
      if (rot.tier !== lastAppliedTier) {
        lastAppliedTier = rot.tier;
        setQty(1);
      }
      d.body.classList.toggle('drawlz-rotation-on', rot.tier > 0);
      heroSwapApply(rot.tier);   /* 0 = One Pair -> all overlays hidden, base hero shows */
    }
    function renderView(view) {
      var size = state.size;
      view.box.classList.toggle('is-gated', !size);
      if (!size) {
        if (view.check1) view.check1.checked = false;
        if (view.check2) view.check2.checked = false;
        setRow(view, 1, false, false); setRow(view, 2, false, false);
        setMsg(view.gate, tpl.gate); setMsg(view.msg1, null); setMsg(view.msg2, null);
        view.box.classList.remove('is-muted');
        return;
      }
      var a1 = avail(1, size), a2 = avail(2, size);
      var allDead = (!tiers[1] || !a1) && (!tiers[2] || !a2);
      view.box.classList.toggle('is-muted', allDead);
      if (allDead) {
        if (view.check1) view.check1.checked = false;
        if (view.check2) view.check2.checked = false;
        setMsg(view.gate, fill(tpl.allSoldout, null, size));
        setRow(view, 1, false, true); setRow(view, 2, false, true);
        setMsg(view.msg1, null); setMsg(view.msg2, null);
        return;
      }
      setMsg(view.gate, null);
      if (view.check1) view.check1.checked = rot.tier >= 1;
      if (view.check2) view.check2.checked = rot.tier === 2;
      if (tiers[1]) {
        setRow(view, 1, a1, !a1);
        setMsg(view.msg1, a1 ? null : fill(tpl.soldout, tiers[1].name, size));
      }
      if (tiers[2]) {
        var parentOk = both ? (a1 && rot.tier >= 1) : true;
        setRow(view, 2, a2 && parentOk, !a2);
        setMsg(view.msg2, a2 ? null : fill(tpl.soldout, tiers[2].name, size));
      }
    }
    /* Round 18L — choice-card PRESENTATION layer. Pure view code: it reads
       rot.tier + state.size + avail() and paints the cards, exactly as
       renderView paints the checkboxes. Radio model: card N selected ⇔
       rot.tier === N (0 = One Pair = both classic checkboxes unchecked).
       No parent-nesting gate on card 2 — the radio model presents all
       options as siblings by design; the state machine, availability,
       pricing, invalidation and submit are untouched and shared. */
    function setCards(view) {
      if (!view.cards.length) return;
      var size = state.size;
      var selectedIdx = -1;
      view.cards.forEach(function (card) {
        var n = parseInt(card.getAttribute('data-drawlz-rotation-card'), 10);
        var enabled = size ? (n === 0 ? true : avail(n, size)) : false;
        var struck = !!size && n > 0 && !enabled;
        var on = !!size && rot.tier === n;
        card.disabled = !enabled;
        card.classList.toggle('is-selected', on);
        card.classList.toggle('is-disabled', !enabled);
        card.classList.toggle('is-struck', struck);
        card.setAttribute('aria-checked', String(on));
        card.tabIndex = on ? 0 : -1;
        if (on) selectedIdx = n;
        /* per-card sold-out note (owner, 18M follow-up): a struck card SAYS
           why, right above its title — "Sold out in {size}". The card layer
           owns these msg slots (runs after renderView, so it standardizes
           whatever branch wrote there); classic markup has no cards, so its
           long-form messages are untouched. */
        if (n > 0) {
          var msgEl = n === 1 ? view.msg1 : view.msg2;
          setMsg(msgEl, struck ? fill(tpl.cardSoldout, tiers[n] ? tiers[n].name : '', size) : null);
        }
      });
      /* roving tabindex: keep one tab stop even while nothing is selected */
      if (selectedIdx === -1) view.cards[0].tabIndex = 0;
    }
    function render() { views.forEach(function (v) { renderView(v); setCards(v); }); }
    function setNotice(text) { views.forEach(function (v) { setMsg(v.notice, text); }); }
    views.forEach(function (view) {
      if (view.check1) view.check1.addEventListener('change', function () {
        setNotice(null);
        rot.tier = view.check1.checked ? 1 : 0;
        applySelection(); render(); emit();
      });
      if (view.check2) view.check2.addEventListener('change', function () {
        setNotice(null);
        if (view.check2.checked) { rot.tier = 2; }
        else { rot.tier = both ? 1 : 0; }
        applySelection(); render(); emit();
      });
      /* Round 18L — card selection writes rot.tier through the exact same
         sequence the checkbox handlers use: setNotice(null) → rot.tier →
         applySelection() → render() → emit(). Arrow keys move-and-select
         among enabled cards (radio-group semantics); Space/Enter are native
         button activation. Disabled buttons fire no clicks. */
      view.cards.forEach(function (card) {
        card.addEventListener('click', function () {
          var n = parseInt(card.getAttribute('data-drawlz-rotation-card'), 10);
          if (card.disabled || n === rot.tier) return;
          setNotice(null);
          rot.tier = n;
          applySelection(); render(); emit();
        });
        card.addEventListener('keydown', function (e) {
          var k = e.key;
          if (k !== 'ArrowRight' && k !== 'ArrowDown' && k !== 'ArrowLeft' && k !== 'ArrowUp') return;
          e.preventDefault();
          var dir = (k === 'ArrowRight' || k === 'ArrowDown') ? 1 : -1;
          var enabled = view.cards.filter(function (c) { return !c.disabled; });
          if (!enabled.length) return;
          var i = enabled.indexOf(card);
          var next = enabled[((i < 0 ? 0 : i) + dir + enabled.length) % enabled.length];
          next.focus();
          next.click();
        });
      });
    });
    var lastSize = state.size;
    onState(function (s) {
      if (s.size === lastSize) { render(); return; }
      lastSize = s.size;
      var noticeText = null;
      if (rot.tier === 2 && !avail(2, s.size)) {
        if (both && avail(1, s.size)) {
          rot.tier = 1;
          noticeText = fill(tpl.revertTier, tiers[2].name, s.size, tiers[1].name);
        } else {
          var blocker = both && tiers[1] ? tiers[1].name : tiers[2].name;
          rot.tier = 0;
          noticeText = fill(tpl.revert, blocker, s.size);
        }
      } else if (rot.tier === 1 && !avail(1, s.size)) {
        rot.tier = 0;
        noticeText = fill(tpl.revert, tiers[1].name, s.size);
      }
      applySelection();
      render();
      setNotice(noticeText);
      if (noticeText) emit();
    });
  }

  /* ---------- grouped review aggregate sync (Round 11) ---------- */
  function initLooxAgg() {
    var src = d.querySelector('[data-drawlz-loox-count]');
    if (!src) return;
    var count = parseInt(src.getAttribute('data-drawlz-loox-count'), 10);
    var rating = src.getAttribute('data-drawlz-loox-rating');
    if (!count || !rating) return;
    d.querySelectorAll('[data-drawlz-agg]').forEach(function (el) {
      var t = el.textContent;
      if (/[\d.]+\s*·\s*\d+/.test(t)) {
        el.textContent = t.replace(/[\d.]+\s*·\s*\d+/, rating + ' · ' + count);
      } else {
        el.textContent = rating + ' · ' + count + ' ' + (el.getAttribute('data-drawlz-reviews-word') || 'reviews');
      }
    });
  }

  /* ---------- sticky CTA visibility (owner rule, Round 7) ---------- */
  function initSticky() {
    var bar = d.querySelector('[data-drawlz-sticky]');
    if (!bar) return;
    var inlineCta = d.querySelector('[data-drawlz-inline-atc]');
    var secondClose = d.querySelector('[data-drawlz-second-close]');
    var atcPassed = false, closeIn = false, closePassed = false;
    function apply() {
      var show = atcPassed && !closeIn && !closePassed;
      bar.classList.toggle('is-shown', show);
      bar.setAttribute('aria-hidden', String(!show));
    }
    function track() {
      if (inlineCta) { atcPassed = inlineCta.getBoundingClientRect().bottom < 0; }
      if (secondClose) {
        var r = secondClose.getBoundingClientRect();
        closeIn = r.top < window.innerHeight && r.bottom > 0;
        closePassed = r.bottom <= 0;
      }
      apply();
    }
    var raf = 0;
    window.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = 0; track(); });
    }, { passive: true });
    window.addEventListener('resize', track);
    track();
  }

  /* ---------- height-aware sticky GALLERY column (Round 8) ---------- */
  function initStickyGallery() {
    var col = d.querySelector('[data-drawlz-sticky-gallery]');
    if (!col) return;
    function measure() {
      var fits = col.offsetHeight + 96 + 24 <= window.innerHeight;
      col.classList.toggle('is-sticky', fits);
    }
    window.addEventListener('resize', measure);
    if ('ResizeObserver' in window) {
      new ResizeObserver(measure).observe(col);
    }
    measure();
  }

  /* ---------- description truncation (Round 8) ---------- */
  function initDescription() {
    d.querySelectorAll('[data-drawlz-desc]').forEach(function (body) {
      var toggle = body.parentElement.querySelector('[data-drawlz-desc-toggle]');
      if (!toggle || !body.classList.contains('is-clamped')) return;
      var collapsedPx = 0;
      function collapsedHeight() {
        var first = body.firstElementChild;
        if (!first) return Math.min(body.scrollHeight, 120);
        return Math.round(first.getBoundingClientRect().bottom - body.getBoundingClientRect().top);
      }
      function measure() {
        var open = toggle.getAttribute('aria-expanded') === 'true';
        collapsedPx = collapsedHeight();
        if (body.scrollHeight <= collapsedPx + 24) {
          body.classList.remove('is-clamped');
          body.style.maxHeight = '';
          toggle.hidden = true;
          return;
        }
        toggle.hidden = false;
        body.style.maxHeight = (open ? body.scrollHeight : collapsedPx) + 'px';
      }
      toggle.addEventListener('click', function () {
        var open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(open));
        toggle.textContent = open ? toggle.getAttribute('data-label-less') : toggle.getAttribute('data-label-more');
        body.style.maxHeight = (open ? body.scrollHeight : collapsedPx) + 'px';
      });
      window.addEventListener('resize', measure);
      measure();
    });
  }

  /* ---------- second-close swatch mirror (owner decision) ---------- */
  function initSwatchMirror() {
    var mount = d.querySelector('[data-drawlz-swatches-mirror]');
    if (!mount) return;
    var srcLabel = d.querySelector('.drawlz-pdp-hero .drawlz-swatches__label');
    var srcDots = d.querySelector('.drawlz-pdp-hero .drawlz-swatches');
    if (!srcDots) return;
    if (srcLabel) mount.appendChild(srcLabel.cloneNode(true));
    var dots = srcDots.cloneNode(true);
    dots.classList.add('drawlz-swatches--center');
    dots.querySelectorAll('[data-drawlz-swatch]').forEach(function (a) { a.setAttribute('data-origin', 'close'); });
    dots.querySelectorAll('[data-shopify-editor-block]').forEach(function (el) { el.removeAttribute('data-shopify-editor-block'); });
    mount.appendChild(dots);
  }

  /* ---------- horizontal-scroll fade affordances (Round 17) ---------- */
  function initScrollFades() {
    d.querySelectorAll('[data-drawlz-scrollfade]').forEach(function (wrap) {
      var scroller = wrap.querySelector('[data-drawlz-scrollfade-scroller]');
      if (!scroller) return;
      var right = wrap.querySelector('[data-drawlz-fade-right]');
      var left = wrap.querySelector('[data-drawlz-fade-left]');
      function update() {
        var more = scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 8;
        if (right) right.classList.toggle('is-visible', more);
        if (left) left.classList.toggle('is-visible', scroller.scrollLeft > 8);
      }
      scroller.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---------- Difference answer expand (Round 18B) ---------- */
  function initDiffAnswers() {
    d.querySelectorAll('[data-drawlz-diff-answer]').forEach(function (wrap) {
      var p = wrap.querySelector('p');
      var btn = wrap.querySelector('[data-drawlz-diff-toggle]');
      if (!p || !btn) return;
      function measure() {
        if (btn.getAttribute('aria-expanded') === 'true') return;
        btn.hidden = p.scrollHeight <= p.clientHeight + 1;
      }
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', String(open));
        btn.textContent = open ? '−' : '+';
        btn.setAttribute('aria-label', open ? 'Hide full answer' : 'Show full answer');
        wrap.classList.toggle('is-open', open);
        p.style.maxHeight = open ? p.scrollHeight + 'px' : '';
      });
      window.addEventListener('resize', measure);
      measure();
    });
  }

  /* ---------- recommendations: compact cards + quick-add modal (18E) ----
     Cross-category cards sell OTHER products without leaving the page.
     Round 18E contract (reference: Mack Weldon): the card is STATELESS —
     image + Add+ chip + title + price. The chip opens the section's
     shared quick-add modal (the drawlz-modal component supplies backdrop,
     focus trap, Esc/backdrop close, scroll lock, focus return — the chip
     carries BOTH data-drawlz-rec-open, which populates the mount, and
     data-drawlz-modal-open, which the component's opener reads; both fire
     on the same click, populate first because initRecs binds before
     initModals in boot). The modal owns ALL variant selection: color
     swatches (name-detected server-side) with the chosen name beside the
     label, size PILLS cross-filtered by the chosen color, and a two-state
     CTA identical to the main PDP button. Picking a color swaps the
     modal image to that colorway's variant image — every colorway image
     preloads on open; a color with no variant image falls back to the
     product's featured image, never a blank. No-variant products add
     straight from the card (no modal). Adding closes the modal and
     renders through the theme cart drawer — same fetch contract, global
     lock, and inline error + Retry as the main CTA.
     NOT filtered by the PDP size selection. */
  var qaCard = null; // the card whose product currently owns the modal
  function recState(card) {
    if (card.__recState) return card.__recState;
    var dataEl = card.querySelector('[data-drawlz-rec-data]');
    if (!dataEl) return null;
    var parsed;
    try { parsed = JSON.parse(dataEl.textContent); } catch (err) { return null; }
    card.__recState = {
      colorIndex: parsed.colorIndex, count: parsed.optionCount,
      sizeIndex: (parsed.sizeIndex == null ? -1 : parsed.sizeIndex),
      variants: parsed.variants || [], featuredImg: parsed.featuredImg || null,
      sel: [], variantId: null, price: null
    };
    return card.__recState;
  }
  function recMatch(st, probe) {
    return st.variants.filter(function (v) {
      for (var i = 0; i < probe.length; i++) {
        if (probe[i] != null && v.opts[i] !== probe[i]) return false;
      }
      return true;
    });
  }
  function recValueAvailable(st, optIndex, value) {
    var probe = st.sel.slice();
    probe[optIndex] = value;
    return recMatch(st, probe).some(function (v) { return v.available; });
  }
  function qaMount(card) {
    var root = card.closest('[data-drawlz-recs]');
    return root ? root.querySelector('[data-drawlz-qa-mount]') : null;
  }
  function qaOpen(card) {
    var mount = qaMount(card);
    var tpl = card.querySelector('[data-drawlz-qa-template]');
    var st = recState(card);
    if (!mount || !tpl || !st) return;
    qaCard = card;
    mount.innerHTML = '';
    mount.appendChild(tpl.content.cloneNode(true));
    // preselect the first color with any purchasable variant — same model
    // as the buy column: color is the starting point, size is the ask
    if (st.colorIndex >= 0 && st.sel[st.colorIndex] == null) {
      var first = Array.prototype.map.call(mount.querySelectorAll('[data-drawlz-qa-swatch]'), function (s) {
        return s.getAttribute('data-value');
      }).filter(function (v) { return recValueAvailable(st, st.colorIndex, v); })[0];
      if (first) st.sel[st.colorIndex] = first;
    }
    // preload EVERY colorway image once — the swap must be instant
    if (!card.__qaPreload) {
      card.__qaPreload = true;
      var seen = {};
      st.variants.forEach(function (v) {
        if (v.img && !seen[v.img]) { seen[v.img] = 1; new Image().src = v.img; }
      });
      if (st.featuredImg && !seen[st.featuredImg]) { new Image().src = st.featuredImg; }
    }
    qaRefresh(card);
  }
  function qaRefresh(card) {
    var mount = qaMount(card);
    var st = recState(card);
    if (!mount || !st) return;
    // a selection invalidated by a later choice resets to unchosen
    for (var k = 0; k < st.count; k++) {
      if (k !== st.colorIndex && st.sel[k] != null && !recValueAvailable(st, k, st.sel[k])) st.sel[k] = null;
    }
    mount.querySelectorAll('[data-drawlz-qa-swatch]').forEach(function (sw) {
      var idx = parseInt(sw.getAttribute('data-opt-index'), 10);
      var val = sw.getAttribute('data-value');
      sw.setAttribute('aria-pressed', String(st.sel[idx] === val));
      var ok = recValueAvailable(st, idx, val);
      sw.classList.toggle('is-sold-out', !ok);
      sw.disabled = !ok;
      sw.setAttribute('aria-label', ok ? val : val + ' — Sold out');
    });
    var nameEl = mount.querySelector('[data-drawlz-qa-optval]');
    if (nameEl && st.colorIndex >= 0) nameEl.textContent = st.sel[st.colorIndex] || '';
    // the 18E image contract: the chosen color's VARIANT image, else the
    // product's featured image — the image always tracks the swatch, the
    // ring never moves alone
    if (st.colorIndex >= 0 && st.sel[st.colorIndex]) {
      var img = mount.querySelector('[data-drawlz-qa-img]');
      var chosen = st.sel[st.colorIndex];
      var vi = st.variants.filter(function (v) { return v.img && v.opts[st.colorIndex] === chosen; })[0];
      var src = (vi && vi.img) || st.featuredImg;
      if (img && src && img.getAttribute('src') !== src) {
        img.removeAttribute('srcset');
        img.setAttribute('src', src);
      }
    }
    mount.querySelectorAll('[data-drawlz-qa-size]').forEach(function (pill) {
      var idx = parseInt(pill.getAttribute('data-opt-index'), 10);
      var val = pill.getAttribute('data-value');
      pill.setAttribute('aria-pressed', String(st.sel[idx] === val));
      var ok = recValueAvailable(st, idx, val);
      pill.classList.toggle('is-sold-out', !ok);
      pill.disabled = !ok;
      pill.setAttribute('aria-label', ok ? val : val + ' — Sold out');
    });
    var complete = true;
    for (var i = 0; i < st.count; i++) { if (st.sel[i] == null) complete = false; }
    st.variantId = null; st.price = null;
    if (complete) {
      var hit = recMatch(st, st.sel)[0];
      if (hit && hit.available) { st.variantId = hit.id; st.price = hit.price || null; }
    }
    // two-state CTA — identical contract to the main PDP button
    var cta = mount.querySelector('[data-drawlz-qa-cta]');
    if (cta && !cta.classList.contains('is-adding')) {
      var active = !!st.variantId;
      cta.classList.toggle('is-active', active);
      cta.setAttribute('aria-disabled', String(!active));
      var label = active ? cta.getAttribute('data-label-active') : cta.getAttribute('data-label-inactive');
      if (active && st.price) label = label.replace('{price}', st.price);
      cta.textContent = label;
    }
  }
  function qaError(mount, text) {
    var err = mount.querySelector('[data-drawlz-qa-err]');
    if (!err) return;
    err.hidden = false;
    err.textContent = text || 'Couldn’t add to cart. ';
    var retry = d.createElement('button');
    retry.type = 'button';
    retry.className = 'drawlz-recs__retry';
    retry.setAttribute('data-drawlz-qa-retry', '');
    retry.textContent = 'Retry';
    err.appendChild(retry);
  }
  function qaAdd(card) {
    var mount = qaMount(card);
    var st = recState(card);
    if (!mount || !st || adding || !st.variantId) return;
    var cta = mount.querySelector('[data-drawlz-qa-cta]');
    var err = mount.querySelector('[data-drawlz-qa-err]');
    setAdding(true);
    if (cta) {
      cta.classList.add('is-adding');
      var la = cta.getAttribute('data-label-adding');
      if (la) cta.textContent = la;
    }
    if (err) { err.hidden = true; err.textContent = ''; }
    var body = new FormData();
    body.set('id', String(st.variantId));
    body.set('quantity', '1');
    body.append('sections', 'cart-drawer,cart-icon-bubble');
    body.append('sections_url', window.location.pathname);
    fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (res) { return res.json().then(function (json) { return { ok: res.ok, json: json }; }); })
      .then(function (r) {
        if (!r.ok || r.json.status) {
          qaError(mount, r.json && r.json.description ? String(r.json.description) + ' ' : null);
          return;
        }
        // success: close the modal, then hand off to the theme's drawer
        closeModal(mount.closest('.drawlz-modal'));
        var drawer = d.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.classList.remove('is-empty');
          drawer.renderContents(r.json);
        }
        // brief confirmation on the card's add bar (18F: the whole card is
        // the button — swap the BAR's text, never the card's innerHTML)
        var chip = card.querySelector('[data-drawlz-rec-bar]');
        if (chip) {
          var orig = chip.getAttribute('data-label') || chip.textContent;
          chip.textContent = chip.getAttribute('data-label-added') || 'Added ✓';
          chip.classList.add('is-added');
          setTimeout(function () { chip.textContent = orig; chip.classList.remove('is-added'); }, 1800);
        }
      })
      .catch(function () { qaError(mount, null); })
      .finally(function () {
        setAdding(false);
        if (cta) { cta.classList.remove('is-adding'); }
        qaRefresh(card); // restores the CTA label (error path keeps the modal open)
      });
  }
  /* instant add for no-variant products — the chip on the card adds directly */
  function recCardError(card, text, variantId) {
    var err = card.querySelector('[data-drawlz-rec-err]');
    if (!err) return;
    err.hidden = false;
    err.textContent = text || 'Couldn’t add to cart. ';
    var retry = d.createElement('button');
    retry.type = 'button';
    retry.className = 'drawlz-recs__retry';
    retry.setAttribute('data-drawlz-rec-retry', '');
    retry.setAttribute('data-variant-id', variantId);
    retry.textContent = 'Retry';
    err.appendChild(retry);
  }
  function recAdd(card, variantId) {
    if (adding || !variantId) return;
    var btn = card.querySelector('[data-drawlz-rec-add]');
    setAdding(true);
    if (btn) btn.classList.add('is-adding');
    var err = card.querySelector('[data-drawlz-rec-err]');
    if (err) { err.hidden = true; err.textContent = ''; }
    var body = new FormData();
    body.set('id', String(variantId));
    body.set('quantity', '1');
    body.append('sections', 'cart-drawer,cart-icon-bubble');
    body.append('sections_url', window.location.pathname);
    fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (res) { return res.json().then(function (json) { return { ok: res.ok, json: json }; }); })
      .then(function (r) {
        if (!r.ok || r.json.status) {
          recCardError(card, r.json && r.json.description ? String(r.json.description) + ' ' : null, variantId);
          return;
        }
        var drawer = d.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.classList.remove('is-empty');
          drawer.renderContents(r.json);
        }
        // the card IS the button (18F) — confirm on its add bar
        var bar = card.querySelector('[data-drawlz-rec-bar]');
        if (bar) {
          var orig = bar.getAttribute('data-label') || bar.textContent;
          bar.textContent = bar.getAttribute('data-label-added') || 'Added ✓';
          bar.classList.add('is-added');
          setTimeout(function () { bar.textContent = orig; bar.classList.remove('is-added'); }, 1800);
        }
      })
      .catch(function () { recCardError(card, null, variantId); })
      .finally(function () {
        setAdding(false);
        if (btn) btn.classList.remove('is-adding');
      });
  }
  /* dynamic slots (Round 18H): pinned picks always show; the remaining
     slots (up to data-drawlz-rec-total) fill from server-rendered HIDDEN
     candidates — Last Chance first, cross-line fallback when Last Chance
     has nothing in the shopper's size. Re-evaluated on every size change
     via the shared size state, so the rail never runs empty. */
  function recDynSizeOk(card, size) {
    var st = recState(card);
    if (!st) return false;
    // no size chosen yet, or the product has no size-role option →
    // any purchasable variant qualifies
    if (!size || st.sizeIndex < 0) {
      return st.variants.some(function (v) { return v.available; });
    }
    return st.variants.some(function (v) { return v.available && v.opts[st.sizeIndex] === size; });
  }
  function recDynFill(size) {
    d.querySelectorAll('[data-drawlz-recs]').forEach(function (root) {
      var total = parseInt(root.getAttribute('data-drawlz-rec-total'), 10) || 10;
      var pinned = root.querySelectorAll('[data-drawlz-rec]:not([data-drawlz-rec-dyn])').length;
      var slots = Math.max(0, total - pinned);
      var lc = [], fb = [];
      root.querySelectorAll('[data-drawlz-rec-dyn]').forEach(function (card) {
        (card.getAttribute('data-drawlz-rec-dyn') === 'lc' ? lc : fb).push(card);
      });
      var shown = 0;
      lc.forEach(function (card) {
        var ok = shown < slots && recDynSizeOk(card, size);
        card.hidden = !ok;
        if (ok) shown++;
      });
      // Last Chance dry in this size → the fallback pool keeps the rail full
      fb.forEach(function (card) {
        var ok = shown < slots && recDynSizeOk(card, size);
        card.hidden = !ok;
        if (ok) shown++;
      });
      // dots track the visible card count
      var visible = pinned + shown;
      root.querySelectorAll('[data-drawlz-recs-dot]').forEach(function (dt, i) {
        dt.hidden = i >= visible;
      });
    });
    // re-run the fits-statically dot check + edge fades against the new set
    window.dispatchEvent(new Event('resize'));
  }
  function initRecs() {
    // refill the dynamic slots on boot and on every size change (onState
    // invokes immediately with the current state)
    onState(function (s) { recDynFill(s.size); });
    d.addEventListener('click', function (e) {
      var open = e.target.closest('[data-drawlz-rec-open]');
      if (open) {
        // populate the shared modal BEFORE the modal component's opener
        // (bound later in boot) shows it on this same click
        if (adding) { e.preventDefault(); e.stopImmediatePropagation(); return; }
        qaOpen(open.closest('[data-drawlz-rec]'));
        return;
      }
      var sw = e.target.closest('[data-drawlz-qa-swatch]');
      if (sw) {
        if (sw.disabled || adding || !qaCard) return;
        var swSt = recState(qaCard);
        if (!swSt) return;
        swSt.sel[parseInt(sw.getAttribute('data-opt-index'), 10)] = sw.getAttribute('data-value');
        qaRefresh(qaCard);
        return;
      }
      var pill = e.target.closest('[data-drawlz-qa-size]');
      if (pill) {
        if (pill.disabled || pill.classList.contains('is-sold-out') || adding || !qaCard) return;
        var pSt = recState(qaCard);
        if (!pSt) return;
        pSt.sel[parseInt(pill.getAttribute('data-opt-index'), 10)] = pill.getAttribute('data-value');
        qaRefresh(qaCard);
        return;
      }
      var cta = e.target.closest('[data-drawlz-qa-cta]');
      if (cta) {
        if (adding || !qaCard || cta.getAttribute('aria-disabled') === 'true') return;
        qaAdd(qaCard);
        return;
      }
      var qretry = e.target.closest('[data-drawlz-qa-retry]');
      if (qretry) {
        if (adding || !qaCard) return;
        qaAdd(qaCard);
        return;
      }
      var addBtn = e.target.closest('[data-drawlz-rec-add]');
      if (addBtn) {
        if (adding) return;
        recAdd(addBtn.closest('[data-drawlz-rec]'), addBtn.getAttribute('data-variant-id'));
        return;
      }
      var retry = e.target.closest('[data-drawlz-rec-retry]');
      if (retry) {
        if (adding) return;
        recAdd(retry.closest('[data-drawlz-rec]'), retry.getAttribute('data-variant-id'));
      }
    });
    // the quick-add modal reuses the shared drawlz-modal component; the
    // snippet takes no class param, so tag it here for the QA panel width
    d.querySelectorAll('[data-drawlz-qa-mount]').forEach(function (m) {
      var modal = m.closest('.drawlz-modal');
      if (modal) modal.classList.add('drawlz-modal--qa');
    });
    // slider: dots + edge fades at both breakpoints (narrow buy column)
    d.querySelectorAll('[data-drawlz-recs]').forEach(function (root) {
      var rail = root.querySelector('[data-drawlz-recs-rail]');
      if (!rail) return;
      // ≤4 tiles fit the column statically (the reference layout) — dots
      // only appear once the rail actually overflows
      var dotsBox = root.querySelector('.drawlz-recs__dots');
      function syncDots() {
        if (dotsBox) dotsBox.hidden = rail.scrollWidth <= rail.clientWidth + 4;
      }
      window.addEventListener('resize', syncDots);
      syncDots();
      var dots = Array.prototype.slice.call(root.querySelectorAll('[data-drawlz-recs-dot]'));
      function cardW() {
        // first VISIBLE card (dynamic candidates render hidden)
        var c = rail.querySelector('[data-drawlz-rec]:not([hidden])');
        return c ? c.getBoundingClientRect().width + 10 : rail.clientWidth;
      }
      if (dots.length) {
        rail.addEventListener('scroll', function () {
          var i = Math.max(0, Math.min(dots.length - 1, Math.round(rail.scrollLeft / cardW())));
          dots.forEach(function (dt, j) { dt.setAttribute('aria-current', String(j === i)); });
        }, { passive: true });
        dots.forEach(function (dt, i) {
          dt.addEventListener('click', function () { rail.scrollTo({ left: i * cardW(), behavior: scrollBehavior }); });
        });
      }
      root.querySelectorAll('[data-drawlz-recs-arrow]').forEach(function (ar) {
        ar.addEventListener('click', function () {
          var dir = ar.getAttribute('data-drawlz-recs-arrow') === 'prev' ? -1 : 1;
          rail.scrollBy({ left: dir * rail.clientWidth * 0.8, behavior: scrollBehavior });
        });
      });
    });
  }

  /* ---------- comparison: mobile compare toggle (Round 18M) ----------
     Below 768px the 3-column table is unreadable — the shopper picks ONE
     competitor line and the CSS shows a clean 2-column table (current
     line vs the pick). The buttons only render/display on mobile; the
     active line lives in data-drawlz-compare-active on the section root. */
  function initComparisonToggle() {
    d.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-drawlz-compare-btn]');
      if (!btn) return;
      var root = btn.closest('[data-drawlz-comparison]');
      if (!root) return;
      root.setAttribute('data-drawlz-compare-active', btn.getAttribute('data-drawlz-compare-btn'));
      root.querySelectorAll('[data-drawlz-compare-btn]').forEach(function (b) {
        b.setAttribute('aria-pressed', String(b === btn));
      });
      window.dispatchEvent(new Event('resize')); // edge fades re-measure
    });
  }

  /* ---------- Round Out the Drawer: mobile placement (Round 18M) ----------
     Design review: on mobile the band arrives too early (right under the
     hero). The template order is editor-owned, so on boot at mobile widths
     the band ELEMENT moves to sit after the Reviews section, before the
     second close. A DOM move keeps every bound listener (they travel with
     the node) and the quick-add modal lives INSIDE the band, so nothing
     re-binds. Applied once at boot — no live reflow on resize (a mid-
     session breakpoint cross keeps the boot-time placement). Desktop
     placement is untouched. */
  function initRecBandMobile() {
    if (!window.matchMedia || !window.matchMedia('(max-width: 1079px)').matches) return;
    var band = d.querySelector('.drawlz-recs-band');
    if (!band) return;
    var reviews = d.querySelector('.drawlz-pdp-reviews');
    if (!reviews) return;
    var anchor = reviews.closest('.shopify-section') || reviews;
    if (!anchor.parentNode) return;
    anchor.parentNode.insertBefore(band, anchor.nextSibling);
  }

  /* ---------- scroll links ---------- */
  function initScrollLinks() {
    d.addEventListener('click', function (e) {
      var link = e.target.closest('[data-drawlz-scroll-to]');
      if (!link) return;
      var target = d.getElementById(link.getAttribute('data-drawlz-scroll-to'));
      if (!target) return;
      e.preventDefault();
      var y = target.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top: y, behavior: scrollBehavior });
    });
    d.querySelectorAll('[data-drawlz-scroll-to]').forEach(function (link) {
      if (d.getElementById(link.getAttribute('data-drawlz-scroll-to'))) return;
      link.hidden = true;
      var prev = link.previousElementSibling;
      if (prev && prev.classList.contains('drawlz-pdp-hero__utility-sep')) prev.hidden = true;
    });
  }

  /* ---------- colorway continuity (Part 4.5) ---------- */
  function initColorways() {
    d.addEventListener('click', function (e) {
      var link = e.target.closest('[data-drawlz-swatch]');
      if (!link) return;
      /* size-aware availability (Round 19): a dimmed swatch never
         navigates — the colorway has no purchasable variant in the
         selected size (class set by the onState sync below) */
      if (link.classList.contains('is-unavailable')) { e.preventDefault(); return; }
      if (adding) { e.preventDefault(); return; }
      if (!state.size) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      try {
        var url = new URL(link.href, location.origin);
        url.searchParams.set('size', state.size);
        if (link.getAttribute('data-origin') === 'close') url.hash = 'second-close';
        e.preventDefault();
        window.location.assign(url.toString());
      } catch (err) { /* fall through */ }
    });
    var prefetched = {};
    function prefetch(link) {
      var href = link.getAttribute('href');
      if (!href || prefetched[href]) return;
      prefetched[href] = true;
      var l = d.createElement('link'); l.rel = 'prefetch'; l.href = href; d.head.appendChild(l);
      var img = link.getAttribute('data-featured-image');
      if (img) { var i = new Image(); i.src = img; }
    }
    d.addEventListener('mouseenter', function (e) {
      if (e.target && e.target.closest) { var s = e.target.closest('[data-drawlz-swatch]'); if (s) prefetch(s); }
    }, true);
    d.addEventListener('touchstart', function (e) {
      var s = e.target.closest && e.target.closest('[data-drawlz-swatch]'); if (s) prefetch(s);
    }, { passive: true });

    /* ---------- size-aware swatch availability (Round 19) ----------
       Every SIBLING swatch link carries data-available-sizes, emitted by
       the snippet from that sibling product's OWN variants (colorways are
       separate products, never variants of the current one). On every
       shared-size change: links missing the selected size dim in place —
       aria-disabled, out of the tab order, click blocked above, label
       says sold out in that size — never hidden or removed. Clearing the
       size restores everything. Queries the whole document, so the
       second-close mirror clone stays in sync; the current product's own
       swatch is a span without the attribute and is never touched. */
    onState(function (s) {
      var size = (s.size || '').trim().toLowerCase();
      d.querySelectorAll('[data-drawlz-swatch][data-available-sizes]').forEach(function (a) {
        var ok = true;
        if (size) {
          ok = false;
          var sizes = (a.getAttribute('data-available-sizes') || '').split(',');
          for (var i = 0; i < sizes.length; i++) {
            if (sizes[i].trim().toLowerCase() === size) { ok = true; break; }
          }
        }
        if (!a.getAttribute('data-drawlz-base-aria')) {
          a.setAttribute('data-drawlz-base-aria', a.getAttribute('aria-label') || '');
        }
        a.classList.toggle('is-unavailable', !ok);
        if (ok) {
          a.removeAttribute('aria-disabled');
          a.removeAttribute('tabindex');
          a.setAttribute('aria-label', a.getAttribute('data-drawlz-base-aria'));
        } else {
          a.setAttribute('aria-disabled', 'true');
          a.setAttribute('tabindex', '-1');
          a.setAttribute('aria-label', a.getAttribute('data-drawlz-base-aria') + ' — sold out in ' + s.size);
        }
      });
    });

    /* Two sources of intent, deliberately treated differently:
         carried - ?size= in the URL. EXPLICIT: the shopper clicked a sized link,
                   so if it is unavailable here they are told why.
         stored  - the persisted sitewide underwear size. AMBIENT: they never asked
                   for it on this page, so an unavailable value fails SILENTLY.
                   Warning about a size nobody typed would be noise.
       Ambient only applies on an underwear PDP; see isUnderwearPdp(). */
    var params = new URLSearchParams(location.search);
    var carried = params.get('size');
    var stored = null;
    if (!carried && isUnderwearPdp() && window.DrawlzSize &&
        typeof window.DrawlzSize.get === 'function') {
      stored = window.DrawlzSize.get();
    }
    var want = carried || stored;
    if (want) {
      var pill = d.querySelector('[data-drawlz-size][data-size="' + CSS.escape(want) + '"]');
      if (pill && !pill.classList.contains('is-sold-out') && !pill.disabled) {
        setSize(want, pill.getAttribute('data-variant-id'));
      } else if (pill && carried) {
        var tpl = d.body.getAttribute('data-drawlz-size-msg-template') || 'Size {size} isn’t available in {color}. Choose another size.';
        showSizeMsg(tpl.replace('{size}', carried).replace('{color}', d.body.getAttribute('data-drawlz-colorway') || 'this colorway'));
      }
    }

    /* Sitewide -> PDP while the page is open (size band, another tab, the
       collection rail behind a modal). Loop-safe: the s === state.size bail means
       the setSize() write-back above cannot bounce back through this listener. */
    if (isUnderwearPdp()) {
      d.addEventListener('drawlz:size', function (e) {
        var s = e && e.detail ? e.detail.size : null;
        if (!s || s === state.size) return;
        var p2 = d.querySelector('[data-drawlz-size][data-size="' + CSS.escape(s) + '"]');
        if (p2 && !p2.classList.contains('is-sold-out') && !p2.disabled) {
          setSize(s, p2.getAttribute('data-variant-id'));
        }
      });
    }
    /* ---------- PACK PAGES: sitewide size <-> the UNDERWEAR axis ----------
       Dual-size templates (pdp-capsule, pdp-blessed-capsule, pdp-pack, ...) render
       TWO pill rows and a hidden single-axis proxy. data-drawlz-underwear is "0"
       there on purpose, so everything above sits out: the proxy carries the
       COMBINED value and on this markup the order is "<tee> / <underwear>" (T-Shirt
       Size is row 1, Underwear Size is row 2), which is useless as a sitewide size.

       The axis IS identifiable, just not from the proxy: the hero emits
       aria-label="{{ opt.name }}" on each .drawlz-size__row, so the row literally
       carries its option name. Bind to aria-label="Underwear Size" and the tee row
       is untouchable by construction.

       Pills are driven through their NATIVE click, never by setting aria-pressed,
       so the dual machine still resolves the variant and updates the proxy - add to
       cart keeps working on both axes. */
    (function dualUnderwearSync() {
      var dual = d.querySelector('.drawlz-zodiac-dual');
      if (!dual) return;
      var uwRow = dual.querySelector('.drawlz-size__row[aria-label="Underwear Size"]');
      if (!uwRow) return;                     /* no underwear axis here -> do nothing */

      var DS = window.DrawlzSize;
      function usable() {
        return DS && typeof DS.get === 'function' && typeof DS.set === 'function' &&
               typeof DS.isUnderwearScale === 'function';
      }
      function pill(val) {
        return val ? uwRow.querySelector('.drawlz-size__pill[data-val="' + CSS.escape(val) + '"]') : null;
      }
      function alive(p) { return !!p && !p.disabled && !p.classList.contains('is-sold-out'); }

      /* Re-entrancy guard. DrawlzSize.set() emits drawlz:size SYNCHRONOUSLY, so
         without this the sequence is: shopper clicks a pill -> we write -> emit ->
         our own live listener sees aria-pressed still "false" (the dual machine's
         paint() has not run yet, it listens on the parent and fires after us) ->
         it fires a NESTED synthetic click -> the dual machine processes the same
         pick twice and clicks the proxy twice. Correct final state, but double
         variant resolution and double setSize for no reason. */
      var syncing = false;
      function selectPill(val) {
        var p = pill(val);
        if (!alive(p) || p.getAttribute('aria-pressed') === 'true') return;
        syncing = true;
        try { p.click(); } finally { syncing = false; }
      }

      /* FORWARD: ambient, so silent. No "size unavailable" message - the shopper
         never asked for this size on this page. */
      if (usable()) {
        var want = DS.get();
        if (want && DS.isUnderwearScale([want])) selectPill(want);
      }

      /* REVERSE: an Underwear pill pick becomes the sitewide size. Listener is on
         uwRow, so a T-Shirt Size click can never reach it. get() !== val is
         REQUIRED, not defensive: set() toggles, so re-passing the current value
         would CLEAR the sitewide size. */
      uwRow.addEventListener('click', function (e) {
        if (syncing) return;
        var p = e.target.closest('.drawlz-size__pill');
        if (!alive(p) || !uwRow.contains(p)) return;
        var val = p.getAttribute('data-val');
        if (val && usable() && DS.isUnderwearScale([val]) && DS.get() !== val) DS.set(val);
      });

      /* Sitewide -> this page while it is open (the size band, another tab). */
      d.addEventListener('drawlz:size', function (e) {
        if (syncing) return;
        var s = e && e.detail ? e.detail.size : null;
        if (!s || !usable() || !DS.isUnderwearScale([s])) return;
        selectPill(s);
      });
    })();
    if (location.hash === '#second-close') {
      var close = d.querySelector('[data-drawlz-second-close]');
      if (close) {
        var y = close.getBoundingClientRect().top + window.scrollY - 72;
        window.scrollTo({ top: y, behavior: 'auto' });
      }
    }
  }

  /* ---------- native Color option (Round 20, SuedeSkin Crew) ----------
     Runs only when the size selector emitted a variant map. The <button>
     swatches write the shared color state; the subscriber syncs every
     instance document-wide (the second-close mirror clone included),
     greys colors with no purchasable variant in the selected size (the
     sibling-swatch treatment: dimmed, aria-disabled, out of the tab
     order, never hidden — restored when the constraint lifts), and
     cross-filters the size pills/selects by the selected color so a dead
     Color × Size combination can never be composed. Color preselects at
     boot — color is the starting point, size is the ask (the site-wide
     buy model). */
  function initColorOption() {
    var mapEl = d.querySelector('[data-drawlz-variant-map]');
    if (mapEl) { try { vmap = JSON.parse(mapEl.textContent); } catch (err) { vmap = null; } }
    if (!vmap || !vmap.variants || !vmap.variants.length) { vmap = null; return; }
    d.addEventListener('click', function (e) {
      var sw = e.target.closest('[data-drawlz-color-swatch]');
      if (!sw || adding) return;
      if (sw.classList.contains('is-unavailable') || sw.classList.contains('is-sold-out')) return;
      setColor(sw.getAttribute('data-color'));
    });
    onState(function (s) {
      d.querySelectorAll('[data-drawlz-color-name]').forEach(function (el) { el.textContent = s.color || ''; });
      d.querySelectorAll('[data-drawlz-color-swatch]').forEach(function (sw) {
        var c = sw.getAttribute('data-color');
        sw.setAttribute('aria-pressed', String(c === s.color));
        if (sw.classList.contains('is-sold-out')) return; // dead in every size — server state stands
        var ok = !s.size || vmapAny(c, s.size);
        if (!sw.getAttribute('data-drawlz-base-aria')) {
          sw.setAttribute('data-drawlz-base-aria', sw.getAttribute('aria-label') || '');
        }
        sw.classList.toggle('is-unavailable', !ok);
        if (ok) {
          sw.removeAttribute('aria-disabled');
          sw.removeAttribute('tabindex');
          sw.setAttribute('aria-label', sw.getAttribute('data-drawlz-base-aria'));
        } else {
          sw.setAttribute('aria-disabled', 'true');
          sw.setAttribute('tabindex', '-1');
          sw.setAttribute('aria-label', sw.getAttribute('data-drawlz-base-aria') + ' — sold out in ' + s.size);
        }
      });
      if (s.color) {
        d.querySelectorAll('[data-drawlz-size]').forEach(function (pill) {
          var ok = vmapAny(s.color, pill.getAttribute('data-size'));
          pill.classList.toggle('is-sold-out', !ok);
          pill.disabled = !ok;
          if (ok) pill.removeAttribute('aria-disabled');
          else pill.setAttribute('aria-disabled', 'true');
        });
        d.querySelectorAll('[data-drawlz-size-select] option').forEach(function (opt) {
          if (!opt.value) return;
          opt.disabled = !vmapAny(s.color, opt.value);
        });
      }
    });
    if (!state.color) {
      var dots = d.querySelectorAll('.drawlz-pdp-hero [data-drawlz-color-swatch]');
      for (var i = 0; i < dots.length; i++) {
        var c = dots[i].getAttribute('data-color');
        if (vmapAny(c, null)) { setColor(c); break; }
      }
    }
  }

  /* ---------- gallery (hero section) ---------- */
  function initGallery() {
    d.querySelectorAll('[data-drawlz-gallery]').forEach(function (root) {
      var track = root.querySelector('[data-gallery-track]');
      var slides = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-slide]'));
      var dots = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-dot]'));
      var counter = root.querySelector('[data-gallery-counter]');
      var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-thumb]'));
      var idx = 0;
      /* the VISIBLE subset (Round 20): on native-color products the color
         filter below hides non-matching media — every index mapping (the
         mobile track's scroll position, dot clicks, the counter) runs on
         the visible subset, never the raw media list. Filter inactive →
         visible = everything, byte-for-byte the old behavior. */
      var visible = slides.map(function (s, i) { return i; });
      function visIndex(i) { return visible.indexOf(i); }
      function render() {
        slides.forEach(function (s, i) { s.classList.toggle('is-active', i === idx); });
        thumbs.forEach(function (t, i) { t.setAttribute('aria-current', String(i === idx)); });
        dots.forEach(function (dot, i) { dot.setAttribute('aria-current', String(i === idx)); });
        if (counter) counter.textContent = (visIndex(idx) + 1) + ' / ' + visible.length;
      }
      if (track) track.addEventListener('scroll', function () {
        var i = Math.round(track.scrollLeft / track.clientWidth);
        var g = visible[Math.max(0, Math.min(visible.length - 1, i))];
        if (g != null && g !== idx) { idx = g; render(); }
      }, { passive: true });
      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          var vi = visIndex(i);
          if (vi === -1) return;
          track.scrollTo({ left: vi * track.clientWidth, behavior: scrollBehavior });
        });
      });
      thumbs.forEach(function (t, i) { t.addEventListener('click', function () { idx = i; render(); }); });
      var rail = root.querySelector('[data-gallery-rail]');
      root.querySelectorAll('[data-gallery-chev]').forEach(function (chev) {
        chev.addEventListener('click', function () {
          if (!rail) return;
          var step = rail.clientHeight / 5 + 8;
          rail.scrollBy({ top: chev.getAttribute('data-gallery-chev') === 'up' ? -step : step, behavior: scrollBehavior });
        });
      });
      /* the rail is capped at the stage height and scrolls internally —
         chevrons only show when there is overflow, and each end disables
         at its limit. Re-measured on resize and as slide images load
         (natural-ratio frames change height with the active slide). */
      var stage = root.querySelector('.drawlz-pdp-hero__stage');
      var chevs = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-chev]'));
      function chevState() {
        chevs.forEach(function (c) {
          c.disabled = c.getAttribute('data-gallery-chev') === 'up'
            ? rail.scrollTop <= 2
            : rail.scrollTop + rail.clientHeight >= rail.scrollHeight - 2;
        });
      }
      function sizeRail() {
        if (!rail || !stage) return;
        var h = stage.getBoundingClientRect().height;
        if (h > 40) rail.style.maxHeight = Math.round(h) + 'px';
        var over = rail.scrollHeight > rail.clientHeight + 4;
        chevs.forEach(function (c) { c.hidden = !over; });
        if (over) chevState();
      }
      if (rail) {
        rail.addEventListener('scroll', chevState, { passive: true });
        window.addEventListener('resize', sizeRail);
        slides.forEach(function (s) {
          var img = s.querySelector('img');
          if (img && !img.complete) img.addEventListener('load', sizeRail);
        });
        sizeRail();
      }
      /* media ↔ color filter (Round 20, owner rule): with a native color
         selected, only that color's images show. Bucketing runs on each
         image's ALT TEXT — the word bw → every color; otherwise the
         FIRST color name mentioned in the alt wins (a black-tee-on-a-
         white-background alt reads as Black, not White); an alt naming
         NO color falls to the color with the FEWEST explicitly tagged
         images (the untagged bulk belongs to the base colorway —
         self-configuring, nothing hardcoded; a tie disables the
         fallback and shows those images for every color). Filters
         slides + thumbs + dots together, keeps the active slide valid,
         resyncs the mobile track and the thumb-rail cap. Products
         without a color option never enter. */
      function applyColorFilter(color) {
        if (!vmap || vmap.colorIndex < 0 || !color) return;
        var names = [];
        vmap.variants.forEach(function (v) {
          var n = v.opts[vmap.colorIndex];
          if (names.indexOf(n) === -1) names.push(n);
        });
        var tags = slides.map(function (s) {
          var img = s.querySelector('img');
          var alt = ((img && img.getAttribute('alt')) || '').toLowerCase();
          if (/\bbw\b/.test(alt)) return '*';
          var best = null, bestAt = -1;
          names.forEach(function (n) {
            var at = alt.indexOf(n.toLowerCase());
            if (at !== -1 && (bestAt === -1 || at < bestAt)) { best = n; bestAt = at; }
          });
          return best;
        });
        var counts = {};
        names.forEach(function (n) { counts[n] = 0; });
        tags.forEach(function (t) { if (t && t !== '*') counts[t]++; });
        var def = names[0];
        names.forEach(function (n) { if (counts[n] < counts[def]) def = n; });
        var defTied = names.some(function (n) { return n !== def && counts[n] === counts[def]; });
        var show = slides.map(function (s, i) {
          var t = tags[i];
          return t === '*' || t === color || (t === null && (defTied || color === def));
        });
        if (show.indexOf(true) === -1) show = slides.map(function () { return true; });
        visible = [];
        slides.forEach(function (s, i) {
          s.hidden = !show[i];
          if (thumbs[i]) thumbs[i].hidden = !show[i];
          if (dots[i]) dots[i].hidden = !show[i];
          if (show[i]) visible.push(i);
        });
        if (visIndex(idx) === -1) idx = visible[0];
        render();
        if (track) track.scrollTo({ left: Math.max(0, visIndex(idx)) * track.clientWidth, behavior: 'auto' });
        sizeRail();
      }
      var lastFilterColor = null;
      onState(function (s) {
        if (s.color === lastFilterColor) return;
        lastFilterColor = s.color;
        applyColorFilter(s.color);
      });
      render();
    });
  }

  /* ---------- difference slider dots (mobile, slider mode only) ---------- */
  function initDifference() {
    d.querySelectorAll('[data-drawlz-difference]').forEach(function (root) {
      var rail = root.querySelector('[data-diff-rail]');
      var dots = Array.prototype.slice.call(root.querySelectorAll('[data-diff-dot]'));
      if (!rail || !dots.length) return;
      function cardW() { return rail.clientWidth * 0.62 + 12; }
      rail.addEventListener('scroll', function () {
        var i = Math.max(0, Math.min(dots.length - 1, Math.round(rail.scrollLeft / cardW())));
        dots.forEach(function (dot, j) { dot.setAttribute('aria-current', String(j === i)); });
      }, { passive: true });
      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { rail.scrollTo({ left: i * cardW(), behavior: scrollBehavior }); });
      });
    });
  }

  /* ---------- FAQ accordions (Defect 2) ---------- */
  function initFaq() {
    d.addEventListener('click', function (e) {
      var q = e.target.closest('[data-drawlz-faq-q]');
      if (!q) return;
      var col = q.closest('.drawlz-pdp-faq__cols > div') || q.closest('[data-drawlz-faq]');
      var open = q.getAttribute('aria-expanded') === 'true';
      if (col) col.querySelectorAll('[data-drawlz-faq-q]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      q.setAttribute('aria-expanded', String(!open));
    });
  }

  /* ---------- Loox expand/collapse ---------- */
  function initLoox() {
    d.querySelectorAll('[data-drawlz-loox-toggle]').forEach(function (btn) {
      var wrap = d.getElementById(btn.getAttribute('aria-controls'));
      if (!wrap) { btn.hidden = true; return; }
      btn.addEventListener('click', function () {
        var open = wrap.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', String(open));
        btn.textContent = open ? btn.getAttribute('data-label-hide') : btn.getAttribute('data-label-show');
        if (open) window.dispatchEvent(new Event('resize'));
      });
    });
  }

  /* ---------- modals (one snippet, page-driven) ---------- */
  var lastTrigger = null;
  function initModals() {
    d.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-drawlz-modal-open]');
      if (opener) {
        var modal = d.getElementById(opener.getAttribute('data-drawlz-modal-open'));
        if (!modal) return;
        e.preventDefault();
        lastTrigger = opener;
        modal.classList.add('is-open');
        d.body.classList.add('overflow-hidden');
        var f = modal.querySelector('button, a[href], select, [tabindex]');
        if (f) f.focus();
        return;
      }
      var closer = e.target.closest('[data-drawlz-modal-close]');
      if (closer) closeModal(closer.closest('.drawlz-modal'));
    });
    d.addEventListener('keydown', function (e) {
      var open = d.querySelector('.drawlz-modal.is-open');
      if (!open) return;
      if (e.key === 'Escape') closeModal(open);
      if (e.key === 'Tab') trapTab(e, open.querySelector('.drawlz-modal__panel'));
    });
  }
  function closeModal(modal) {
    if (!modal) return;
    modal.classList.remove('is-open');
    d.body.classList.remove('overflow-hidden');
    if (lastTrigger && lastTrigger.focus) lastTrigger.focus();
  }
  function trapTab(e, panel) {
    if (!panel) return;
    var els = Array.prototype.filter.call(
      panel.querySelectorAll('button, a[href], select, [tabindex]:not([tabindex="-1"])'),
      function (x) { return !x.disabled && x.getClientRects().length > 0; }
    );
    if (!els.length) return;
    var first = els[0], last = els[els.length - 1];
    if (e.shiftKey && d.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && d.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---------- boot ---------- */
  function init() {
    initSizeSelectors();
    initForms();
    initKaching();
    initRotationMirror(); // must clone before initRotation binds the boxes
    initRotation();
    initLooxAgg();
    initSticky();
    initStickyGallery();
    initDescription();
    initSwatchMirror();
    initScrollLinks();
    initColorways();
    initColorOption(); // after the mirror clone exists; loads the variant map
    initGallery();
    initDifference();
    initDiffAnswers();
    initRecBandMobile(); // move the band BEFORE fades/sliders measure it
    initRecs(); // binds before initModals: quick-add populate precedes open
    initComparisonToggle();
    initScrollFades();
    initFaq();
    initLoox();
    initModals();
  }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', init);
  else init();
})();